"use client";

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useVendor } from "@/context/VendorContext";

interface CartContextType {
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { currentVendorCart } = useVendor();

  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);
  const toggleCart = useCallback(() => setIsCartOpen((prev) => !prev), []);

  // ✅ Close cart when it becomes empty (side-effect must be useEffect)
  useEffect(() => {
    if (isCartOpen && (currentVendorCart?.item_count ?? 0) === 0) {
      closeCart();
    }
  }, [currentVendorCart?.item_count, isCartOpen, closeCart]);

  return (
    <CartContext.Provider value={{ isCartOpen, openCart, closeCart, toggleCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextType {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
